import { ChevronDown, Columns3, LayoutGrid, Rows3, Search } from 'lucide-react';
import type { GroupMode } from '@/lib/notes-grouping';
import { cn } from '@/lib/utils';

export type NotesLayout = 'cards' | 'rows' | 'pin';

export interface NotesTab {
  id: string;
  label: string;
  count: number;
}

export interface NotesSubBarProps {
  /** When empty / undefined the tabs row is hidden (project page). */
  tabs?: ReadonlyArray<NotesTab>;
  activeTab?: string;
  onTabChange?: (id: string) => void;

  groupMode: GroupMode;
  /** Available group modes — `'project'` is excluded on the
   *  project-scoped page (Q4 trim). */
  groupOptions: ReadonlyArray<GroupMode>;
  onGroupChange: (next: GroupMode) => void;

  layout: NotesLayout;
  onLayoutChange: (next: NotesLayout) => void;

  search: string;
  onSearchChange: (next: string) => void;
}

const GROUP_LABEL: Record<GroupMode, string> = {
  recency: 'Recency',
  project: 'Project',
  author: 'Author',
};

/**
 * Notes redesign — sub-toolbar.
 *
 * Layout (left → right):
 *   1. Tabs (workspace page only): All / On projects / On tasks +
 *      counts. Hidden on the project page where every note is
 *      already scoped to the current project.
 *   2. Group-by cycler — clicking advances through the available
 *      modes (matches the prototype's single-button cycler).
 *   3. Layout switcher (cards / rows / pin) — segmented icon control.
 *   4. Search input with `⌘K` kbd hint (decorative; opens the global
 *      command palette via that hotkey, see Q7).
 */
export const NotesSubBar = ({
  tabs,
  activeTab,
  onTabChange,
  groupMode,
  groupOptions,
  onGroupChange,
  layout,
  onLayoutChange,
  search,
  onSearchChange,
}: NotesSubBarProps) => {
  const cycleGroup = (): void => {
    if (groupOptions.length <= 1) return;
    const i = groupOptions.indexOf(groupMode);
    onGroupChange(groupOptions[(i + 1) % groupOptions.length] ?? groupOptions[0]!);
  };

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {tabs && tabs.length > 0 ? (
        <div
          role="tablist"
          aria-label="Filter notes by parent type"
          className={cn(
            'inline-flex items-center gap-0.5 rounded-[9px] border p-[3px]',
            'border-border bg-secondary',
          )}
        >
          {tabs.map((t) => {
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onTabChange?.(t.id)}
                className={cn(
                  'inline-flex h-6 items-center gap-1.5 rounded-md border-0 px-2.5 text-[11.5px] font-medium',
                  active
                    ? 'bg-background text-foreground shadow-[0_1px_2px_oklch(0_0_0/0.06)]'
                    : 'text-foreground-muted hover:text-foreground bg-transparent',
                )}
              >
                {t.label}
                <span
                  className={cn(
                    'rounded-full px-1 text-[10px] tabular-nums',
                    active
                      ? 'bg-[oklch(0_0_0/0.05)] text-foreground-muted dark:bg-[oklch(1_0_0/0.08)]'
                      : 'bg-background text-foreground-faint',
                  )}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <button
        type="button"
        onClick={cycleGroup}
        title="Cycle group-by"
        className={cn(
          'inline-flex h-[30px] items-center gap-1.5 rounded-lg border bg-background px-2.5 text-[12px]',
          'border-border text-foreground hover:bg-[var(--hover-bg,oklch(0.96_0_0))]',
        )}
      >
        <span className="text-foreground-faint">Group:</span>
        <span>{GROUP_LABEL[groupMode]}</span>
        <ChevronDown className="text-foreground-faint size-3" />
      </button>

      <div
        role="group"
        aria-label="Layout"
        className={cn(
          'inline-flex items-center gap-0.5 rounded-[9px] border p-[3px]',
          'border-border bg-secondary',
        )}
      >
        {(
          [
            { id: 'cards' as const, label: 'Cards', Icon: LayoutGrid },
            { id: 'rows' as const, label: 'Rows', Icon: Rows3 },
            { id: 'pin' as const, label: 'Pinboard', Icon: Columns3 },
          ]
        ).map(({ id, label, Icon }) => {
          const active = id === layout;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={active}
              aria-label={label}
              onClick={() => onLayoutChange(id)}
              className={cn(
                'inline-flex size-6 items-center justify-center rounded-md border-0',
                active
                  ? 'bg-background text-foreground shadow-[0_1px_2px_oklch(0_0_0/0.06)]'
                  : 'text-foreground-muted hover:text-foreground bg-transparent',
              )}
            >
              <Icon className="size-3.5" aria-hidden="true" />
            </button>
          );
        })}
      </div>

      <div
        className={cn(
          'ml-auto flex h-[30px] flex-[0_1_320px] items-center gap-2 rounded-lg border border-transparent px-2.5',
          'bg-secondary text-foreground-faint transition-[background-color,border-color] duration-100',
          'focus-within:border-[var(--brand-accent)] focus-within:bg-background',
        )}
      >
        <Search className="size-3.5" aria-hidden="true" />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search title, body, parent…"
          className={cn(
            'text-foreground placeholder:text-foreground-faint h-full flex-1 border-0 bg-transparent text-[12.5px] outline-none',
          )}
        />
        <kbd
          className={cn(
            'border-border bg-background text-foreground-faint inline-flex h-[18px] items-center rounded-[4px] border px-1 font-mono text-[10px]',
          )}
        >
          ⌘K
        </kbd>
      </div>
    </div>
  );
};
